import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { EdgeFunctionName, UploadUserAvatarParams } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

export const useAvatarUpload = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { id: userId } = useCurrentUser();
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const { toastSuccess, toastError } = useToastWrapper();

  const mutation = useMutation<string, string, FormData>({
    mutationFn: supabaseMutationFn((params) =>
      supabase.functions.invoke<string>(EdgeFunctionName.UploadUserAvatar, {
        body: params as UploadUserAvatarParams,
      })
    ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCurrentUser, userId] }),
        queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetUserProfile, userId] }),
      ]);

      setIsLoading(false);
      toastSuccess('Photo uploaded');
    },
    onError: () => {
      setIsLoading(false);
      toastError(`Can't upload photo`);
    },
  });

  const onPhotoUpload = async (photo: Blob) => {
    try {
      setIsLoading(true);

      const formData = new FormData();
      formData.append('image', photo, 'avatar.jpeg');

      await mutation.mutate(formData);
    } catch {
      setIsLoading(false);
      toastError(`Can't upload photo`);
    }
  };

  return { onPhotoUpload, isLoading, setIsLoading };
};
